import Axios from 'axios'
import { message } from 'antd'

let isRefreshing = false
let failedQueue = []

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token)
    }
  })
  failedQueue = []
}

export const refreshAccessToken = () => {
  const refreshToken = localStorage.getItem(process.env.REACT_APP_REFRESH_TOKEN)

  if (!refreshToken) {
    localStorage.clear()
    window.location.href = '/login'
    return Promise.reject('No refresh token')
  }

  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      failedQueue.push({ resolve, reject })
    })
  }

  isRefreshing = true

  return Axios.post(
    `${process.env.REACT_APP_API}contas/renovar-token`,
    { refreshToken }
  ).then(response => {
    const { token, refreshToken: newRefreshToken } = response.data.result
    localStorage.setItem(process.env.REACT_APP_AUTH_TOKEN, token)
    localStorage.setItem(process.env.REACT_APP_REFRESH_TOKEN, newRefreshToken)
    processQueue(null, token)
    return token
  }).catch(error => {
    processQueue(error, null)
    localStorage.clear()
    window.location.href = '/login'
    return Promise.reject(error)
  }).finally(() => {
    isRefreshing = false
  })
}

const mergeCommonHeaders = headers => {
  const authToken = localStorage.getItem(process.env.REACT_APP_AUTH_TOKEN)

  return authToken
    ? { ...headers, Authorization: `Bearer ${authToken}` }
    : headers
}

const AxiosClient = Axios.create({
  baseURL: process.env.REACT_APP_API,
})

AxiosClient.interceptors.request.use(
  function (config) {
    config.headers.common = mergeCommonHeaders(config.headers.common)
    return config
  },
  function (error) {
    return Promise.reject(error)
  }
)

AxiosClient.interceptors.response.use(
  function (response) {
    if (!response) {
      message.error('Ocorreu um erro inesperado...')
      return
    }

    const data = response.data
    if (data.isSuccess === false) {
      message.error(data.errorMessage)
    }

    return data.result
  },
  async function (error) {
    const originalRequest = error.config

    if (error.response?.status === 401 && !originalRequest._retry) {
      const refreshToken = localStorage.getItem(process.env.REACT_APP_REFRESH_TOKEN)

      if (!refreshToken) {
        localStorage.clear()
        window.location.href = '/login'
        return new Promise(() => {})
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        })
          .then(token => {
            originalRequest.headers['Authorization'] = `Bearer ${token}`
            return AxiosClient(originalRequest)
          })
          .catch(err => Promise.reject(err))
      }

      originalRequest._retry = true
      isRefreshing = true

      try {
        const response = await Axios.post(
          `${process.env.REACT_APP_API}contas/renovar-token`,
          { refreshToken }
        )

        const { token, refreshToken: newRefreshToken } = response.data.result

        localStorage.setItem(process.env.REACT_APP_AUTH_TOKEN, token)
        localStorage.setItem(process.env.REACT_APP_REFRESH_TOKEN, newRefreshToken)

        processQueue(null, token)

        originalRequest.headers['Authorization'] = `Bearer ${token}`
        return AxiosClient(originalRequest)
      } catch (refreshError) {
        processQueue(refreshError, null)
        localStorage.clear()
        window.location.href = '/login'
        return new Promise(() => {})
      } finally {
        isRefreshing = false
      }
    }

    if (!error.response) return Promise.reject('Ocorreu um erro inesperado...')
    return Promise.reject(error.response.data.errorMessage || 'Ocorreu um erro inesperado...')
  }
)

const MiddlewareConfig = {
  returnRejectedPromiseOnError: true,
  interceptors: {
    request: [
      {
        success: function ({ getState, dispatch, getSourceAction }, request) {
          request.headers.common = mergeCommonHeaders(request.headers.common)
          return request
        },
        error: function ({ getState, dispatch, getSourceAction }, error) {
          return error
        }
      }
    ],
    response: [
      {
        success: function ({ getState, dispatch, getSourceAction }, req) {
          return req
        },
        error: function ({ getState, dispatch, getSourceAction }, error) {
          message.error(error)
          return Promise.reject(error)
        }
      }
    ]
  }
}

export default {
  AxiosClient,
  MiddlewareConfig,
  mergeCommonHeaders
}
